import { optionalPositiveInteger, optionalTrimmedString } from '../../../lib/routeSearch';
import type { CreativeTab } from './model';
import { CreativeStatus, defaultCreativesFilter, type CreativesFilter } from './model';

export type CreativesRouteSearch = {
  agencies?: string;
  brand?: string;
  search?: string;
  statuses?: string;
  page?: number;
  size?: number;
};

export type CreativesRouteFilters = Pick<
  CreativesFilter,
  'brandId' | 'name' | 'activeStatus' | 'page' | 'size'
> & { agencyIds?: string[] };

export function creativesRouteSearchSchema(search: Record<string, unknown>): CreativesRouteSearch {
  const agencies = optionalTrimmedString(search['agencies']);
  const brand = optionalTrimmedString(search['brand']);
  const name = optionalTrimmedString(search['search']);
  const statuses = typeof search['statuses'] === 'string' ? search['statuses'].trim() : undefined;
  const page = optionalPositiveInteger(search['page']);
  const size = optionalPositiveInteger(search['size']);
  return {
    ...(agencies ? { agencies } : {}),
    ...(brand ? { brand } : {}),
    ...(name ? { search: name } : {}),
    ...(statuses !== undefined ? { statuses } : {}),
    ...(page ? { page } : {}),
    ...(size ? { size } : {}),
  };
}

export function parseCreativesRouteSearch(
  search: CreativesRouteSearch,
  tab: CreativeTab,
): CreativesRouteFilters {
  const defaults = defaultCreativesFilter(tab);
  return {
    ...(search.agencies
      ? {
          agencyIds: search.agencies
            .split(',')
            .map((id) => id.trim())
            .filter(Boolean),
        }
      : {}),
    brandId: search.brand ?? '',
    name: search.search ?? defaults.name,
    activeStatus: parseStatuses(search.statuses, defaults.activeStatus),
    page: search.page ? search.page - 1 : defaults.page,
    size: search.size ?? defaults.size,
  };
}

export function serializeCreativesRouteSearch(
  filters: CreativesRouteFilters,
  tab: CreativeTab,
): CreativesRouteSearch {
  const defaults = defaultCreativesFilter(tab);
  return {
    ...(filters.agencyIds?.length ? { agencies: filters.agencyIds.join(',') } : {}),
    ...(filters.brandId ? { brand: filters.brandId } : {}),
    ...(filters.name.trim() ? { search: filters.name.trim() } : {}),
    ...(sameStatuses(filters.activeStatus, defaults.activeStatus)
      ? {}
      : { statuses: filters.activeStatus.join(',') }),
    ...(filters.page ? { page: filters.page + 1 } : {}),
    ...(filters.size !== defaults.size ? { size: filters.size } : {}),
  };
}

const creativeStatuses: ReadonlySet<string> = new Set(Object.values(CreativeStatus));

function parseStatuses(value: string | undefined, defaults: readonly CreativeStatus[]) {
  if (value === undefined) return [...defaults];
  if (!value) return [];
  return value
    .split(',')
    .filter((status): status is CreativeStatus => creativeStatuses.has(status));
}

function sameStatuses(left: readonly CreativeStatus[], right: readonly CreativeStatus[]) {
  return left.length === right.length && left.every((status, index) => status === right[index]);
}
